import { TimeUnits } from '@shared/types'
import { convertToMs } from '@shared/utils'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { isEmpty, uniqBy } from 'lodash'
import { useParams } from 'router-v6'
import { validate } from 'uuid'
import { useAuth } from 'v2/shared/hooks/use-auth'
import { conversationService } from 'v2/shared/services/conversations-service'
import { executionService } from 'v2/shared/services/execution-service'
import { conversationQueryKeys } from 'v2/shared/utils/query-key-management'
import { useConversationStore, useSharedConversationStore } from 'v2/state/conversation'
import { usePhoneStore } from 'v2/state/phone'
import type {
  Channels,
  RActiveConversation,
  RNewConversation,
  ViewLiveConversationsParams,
  ViewRecordsParams,
} from 'v2/types/api/conversations'
import { ChunkOrigin } from 'v2/types/chunk.external'
import { type Chunk, ChunkType, type RConversationDetailInternal } from 'v2/types/chunk.internal'
import { PubnubEvents } from 'v2/types/pubnub'
import { pubnubEventBus } from 'v2/utils/event-bus/pubnub'
import { logger } from 'v2/utils/logger'
import { getProperTimetoken, isInitiatingConversation } from './utils'
import { toChunkModel } from './utils/transformers'

const transformConversations = (conversationsList?: RActiveConversation[]) => {
  const conversationsMap: Record<string, RActiveConversation> = {}
  const contactUuidMap: Record<string, RActiveConversation> = {}

  conversationsList?.forEach((conversation) => {
    conversationsMap[conversation.conversation_number] = conversation
    contactUuidMap[conversation.contact_uuid] = conversation
  })
  return { conversationsList, conversationsMap, contactUuidMap }
}

type Select<T, O> = (data?: T) => O

export const useActiveConversationsQuery = <TOutput = ReturnType<typeof transformConversations>>(
  select: Select<RActiveConversation[], TOutput> = transformConversations as Select<
    RActiveConversation[],
    TOutput
  >,
) =>
  useQuery({
    queryKey: conversationQueryKeys().active,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
    meta: {
      silent: true,
    },
    queryFn: async () => {
      const response = await conversationService.getActiveConversations()
      // Pick the call conversation from the response and set it in the phone store
      // Needed for call channel to function properly
      usePhoneStore.getState().setCallConversation(response.data)
      return response.data
    },
    select,
  })

export const useInvitedContactsQuery = <TOutput = RNewConversation[]>(
  select?: Select<RNewConversation[], TOutput>,
) => {
  const { details } = useAuth()
  return useQuery({
    queryKey: conversationQueryKeys().invited,
    // Since this data (invited contacts) is not used in many components and is not huge we are disabling the structural sharing
    // This stops RQ from doing deep clone checks and get into max callstack exceeded error
    // https://github.com/TanStack/query/issues/3279
    structuralSharing: false,
    enabled: !!details?.aom_uuid,
    meta: {
      silent: true,
    },
    select,
    queryFn: async () => {
      const response = await executionService.getInvitedContacts(details?.aom_uuid as string)
      return response.data
    },
  })
}

interface PageParam {
  apiCursor: number
  pubnubCursor: number
  isInitial: boolean
  readableString: string
}

const getInitialParam = (): PageParam => ({
  apiCursor: Date.now(),
  pubnubCursor: Date.now(),
  readableString: `${dayjs(Date.now()).format('HH:mm A')}`,
  isInitial: true,
})

/**
 * useMessageChunksQuery Hook
 *
 * This hook manages the fetching and pagination of message chunks for a given conversation.
 * It combines messages from both PubNub (for real-time chat) and the API (for historical data).
 *
 * Functionality:
 * 1. Fetches message chunks in based on the cursor values from the previous page.
 * 2. Supports infinite scrolling with backward pagination.
 * 3. Deduplicates messages and sorts them by timestamp.
 * 4. Filters out unknown chunk types.
 * 5. Provides a method to add new chunks to the existing data.
 *
 * Use Cases:
 * - Loading and displaying conversation history in a chat interface.
 * - Implementing infinite scroll for older messages.
 * - Real-time updates of new messages in an active conversation.
 *
 * Assumptions:
 * - PubNub is used for real-time chat messages (ConvChannels.CHAT).
 * - API is used for historical data and non-chat channels.
 * - Each page contains a mix of PubNub and API chunks.
 * - A minimum of 30 chunks (combined PubNub and API) is required to fetch the next page.
 * - Chunks are uniquely identified by their 'id' property.
 * - Chunks have a 'timetoken' property for sorting and pagination.
 * - The conversation object contains a 'conversation_number' and 'channel' property.
 *
 * @param conversation - The active conversation object (optional)
 *
 * @returns An object containing:
 *  - chunks: The sorted and filtered array of message chunks
 *  - addChunk: Function to add a new chunk to the existing data
 *  - fetchNextPage: Function to load the next page of messages
 *  - hasNextPage: Boolean indicating if there are more pages to load
 *  - isFetching: Boolean indicating if a fetch operation is in progress
 */
export const useMessageChunksQuery = (
  conversation?: RActiveConversation | RNewConversation | undefined,
) =>
  useInfiniteQuery<Chunk[], Error, Chunk[], unknown[], PageParam>({
    queryKey: conversationQueryKeys().chunks(conversation?.conversation_number),
    queryFn: async ({ pageParam }) => {
      logger.log(
        `API Cursor at: ${dayjs(pageParam.apiCursor).format('HH:mm A')}, PubNub Cursor at: ${dayjs(pageParam.pubnubCursor).format('HH:mm A')} for ${conversation?.conversation_number}`,
      )
      try {
        const chunks: Chunk[] = []

        // Fetch chunks from the API for all conversation types
        const response = await conversationService.getChunks(
          conversation?.conversation_number as string,
          undefined,
          pageParam.isInitial ? Date.now() : pageParam.apiCursor,
        )
        const fromAPI = (response.data?.chunks || []).map((apiChunk) =>
          toChunkModel(apiChunk, ChunkOrigin.apiFetchChunks, {
            templates: response.data.templates,
          }),
        )

        // TODO_BE: Get template data from API
        if (conversation?.conversation_number && !isEmpty(response.data?.templates))
          useSharedConversationStore.getState().setMeta(conversation?.conversation_number, {
            templates: response.data.templates,
          })

        chunks.push(...fromAPI)

        // // Condition: Check if the conversation channel is CHAT
        // if (conversation?.channel === ConvChannels.CHAT) {
        //   const messages = await fetchMessagesMutation.mutateAsync({
        //     start: getPubnubTimetoken(
        //       pageParam.isInitial ? new Date().getTime() : pageParam.pubnubCursor,
        //     ),
        //     channels: [conversation?.contact_uuid as string],
        //     count: 100,
        //   })
        //   const fromPubnub =
        //     messages?.channels?.[conversation?.contact_uuid as string]?.map((m) =>
        //       toChunkModel({ conversation, pbMessage: m }, ChunkOrigin.pubnubFetchMessages),
        //     ) || []
        //   chunks.push(...fromPubnub)
        // }

        const sortedAndUniqueChunks = uniqBy(chunks, 'id').sort((a, b) =>
          String(a.timetoken).localeCompare(String(b.timetoken)),
        )

        setTimeout(() => {
          pubnubEventBus.emit(PubnubEvents.CHUNKS_FETCHED, {
            conversationNumber: conversation?.conversation_number as string,
          })
        }, 50)

        return sortedAndUniqueChunks
      } catch (error) {
        logger.error('Error fetching messages', error)
        return []
      }
    },
    staleTime: Infinity, // Never consider the data stale
    meta: {
      silent: true,
    },
    enabled:
      !!conversation?.conversation_number &&
      !isInitiatingConversation(conversation as RNewConversation), // Only enable the query if we have a conversation number
    initialPageParam: getInitialParam(),
    // not used, but required by useInfiniteQuery
    getNextPageParam: () => ({
      apiCursor: 0,
      pubnubCursor: 0,
      readableString: '',
      isInitial: false,
    }),
    getPreviousPageParam: (firstPage, allPages) => {
      if (!firstPage) return getInitialParam()
      if (firstPage && firstPage.length === 0) return null

      const isPubnubChunk = (chunk: Chunk) =>
        chunk.origin === ChunkOrigin.pubnubChatEvent ||
        chunk.origin === ChunkOrigin.pubnubFetchMessages

      const { pubnubChunk, apiChunk, pubnubCount, apiCount } = firstPage.reduce(
        (acc, chunk) => {
          if (isPubnubChunk(chunk)) {
            if (!acc.pubnubChunk) acc.pubnubChunk = chunk
            acc.pubnubCount += 1
          } else {
            if (!acc.apiChunk) acc.apiChunk = chunk
            acc.apiCount += 1
          }
          return acc
        },
        {
          pubnubChunk: undefined as Chunk | undefined,
          apiChunk: undefined as Chunk | undefined,
          pubnubCount: 0,
          apiCount: 0,
        },
      )

      // If either count is less than 30, return null
      if (allPages.length > 1 && pubnubCount < 30 && apiCount < 30) return null

      const minTimetoken = Math.min(
        apiChunk ? getProperTimetoken(apiChunk.timetoken) : Infinity,
        pubnubChunk ? getProperTimetoken(pubnubChunk.timetoken) : Infinity,
      )

      return {
        apiCursor: apiChunk ? getProperTimetoken(apiChunk.timetoken) : minTimetoken,
        pubnubCursor: pubnubChunk ? getProperTimetoken(pubnubChunk.timetoken) : minTimetoken,
        readableString: `Api Cursor at: ${dayjs(apiChunk?.timetoken).format('HH:mm A')}, PubNub Cursor at: ${dayjs(getProperTimetoken(pubnubChunk?.timetoken)).format('HH:mm A')}`,
        isInitial: false,
      }
    },
    select: (data) => {
      const uniqueChunks = uniqBy(data.pages.flat(), 'id') // Deduplicate based on id
      return uniqueChunks
        .sort((a, b) => String(a.timetoken).localeCompare(String(b.timetoken)))
        .filter((chunk) => chunk.type !== ChunkType.unknown) // Filter out unknown chunks
    },
  })

// Add these new queries after the existing ones

export const useViewsQuery = () => {
  const { userAuthenticated } = useAuth()
  return useQuery({
    queryKey: conversationQueryKeys().views(),
    enabled: userAuthenticated,
    meta: {
      silent: true,
    },
    queryFn: async () => {
      const response = await conversationService.getViews(undefined)
      return response.data
    },
  })
}

export const useViewDetailsQuery = (uuid: string) => {
  const { userAuthenticated } = useAuth()
  return useQuery({
    queryKey: conversationQueryKeys().views(uuid),
    enabled: userAuthenticated,
    queryFn: async () => {
      const response = await conversationService.getViewDetails(uuid)
      return response.data
    },
    retry(failureCount, error) {
      // Don't retry for client errors (4xx)
      if ('response' in error && error.response) {
        const { status } = error.response as { status: number }
        // Don't retry for client errors (4xx)
        if (status >= 400 && status < 500) return false
        // Retry for server errors (5xx) up to 3 times
        if (status >= 500) return failureCount < 3
      }
      // For network errors or other issues, retry up to 3 times
      return failureCount < 3
    },
  })
}

export const useConversationDetailQuery = (
  conversationNumber: string,
  queryParams?: Record<string, unknown>,
  enabled = true,
) =>
  useQuery<RConversationDetailInternal>({
    queryKey: conversationQueryKeys().conversationDetail(conversationNumber),
    gcTime: convertToMs(10, TimeUnits.Minutes),
    staleTime: convertToMs(20, TimeUnits.Minutes),
    meta: {
      silent: true,
    },
    queryFn: async () => {
      const response = await conversationService.getConversationDetail(
        conversationNumber,
        queryParams || {},
      )
      return {
        ...response.data,
        chunks: response.data.chunks
          .map((c) =>
            toChunkModel(c, ChunkOrigin.apiFetchChunks, { templates: response.data.templates }),
          )
          .reverse()
          .sort((a, b) => String(a.timetoken).localeCompare(String(b.timetoken))),
      }
    },
    enabled,
  })

// Add this new query after the existing ones

export const useViewRecordsQuery = (enabled = true) => {
  const { viewId } = useParams()
  const viewFilters = useConversationStore((state) => viewId && state.viewFilters[viewId])
  const { pagination, ...restViewFilters } = viewFilters || {}
  const viewPayload = {
    ...restViewFilters,
    page: (pagination?.pageIndex || 0) + 1,
  }
  return useQuery({
    queryKey: conversationQueryKeys().viewRecords(
      viewId as string,
      viewPayload as ViewRecordsParams,
    ),
    gcTime: convertToMs(10, TimeUnits.Minutes),
    queryFn: async () => {
      const response = await conversationService.getViewRecords(viewPayload as ViewRecordsParams)
      return response.data
    },
    enabled: enabled && !isEmpty(viewFilters) && validate(viewId as string),
  })
}

export const useConversationFilterConfiguration = () =>
  useQuery({
    queryKey: conversationQueryKeys().conversationFilterConfiguration,
    queryFn: async () => {
      const response = await conversationService.getConversationFilterConfig()
      return response.data
    },
  })

export const useLiveAgentsQuery = (channel?: Channels, staleTime = 0) =>
  useQuery({
    staleTime,
    queryKey: conversationQueryKeys().liveAgents(channel),
    queryFn: async () => {
      const response = await conversationService.getLiveAgents(channel)
      return response.data
    },
  })

export const useLiveTeamsQuery = (channel?: Channels, staleTime = 0) =>
  useQuery({
    staleTime,
    queryKey: conversationQueryKeys().liveTeams(channel),
    queryFn: async () => {
      const response = await conversationService.getLiveTeams(channel)
      return response.data
    },
  })

export const useLiveConversationsQuery = () => {
  const { viewId } = useParams()
  const viewFilters = useConversationStore((state) => viewId && state.viewFilters[viewId])
  const { pagination } = viewFilters || {}

  const fields = [
    'customer_name',
    'conv_created_date',
    'agent_details.agent_name',
    'direction_inbound',
    'parent_channel',
    'conv_number',
    'conv_created_time',
    'conv_total_duration',
    'contact_identifier',
    'conv_child_sender_ids',
  ]
  const payload = {
    client_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    fields,
    filters: {},
    page: (pagination?.pageIndex || 0) + 1,
  }
  return useQuery({
    queryKey: conversationQueryKeys().getLiveConversationInViews(payload),
    gcTime: convertToMs(10, TimeUnits.Minutes),
    refetchInterval: 10000, // Refetch every 10 seconds
    queryFn: async () => {
      const response = await conversationService.getLiveConversationInViews(
        payload as ViewLiveConversationsParams,
      )
      return response.data
    },
  })
}

export const useRecordingQuery = (conv_number: string, enabled = false) =>
  useQuery({
    queryKey: conversationQueryKeys().conversationRecordings(conv_number),
    enabled,
    queryFn: async () => {
      const response = await conversationService.getRecordingDetails(conv_number)
      return response.data
    },
  })
